import SearchIcon from '@mui/icons-material/SearchOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AttendanceMetrics } from './AttendanceMetrics';
import { HistoryNotesPanelProps, HistorySubTab } from './HistoryNotesPanel.props';
import { HistoryTable } from './HistoryTable';
import { ActionPermissions } from '../../../../../@types/permission.type';
import { HIDDEN_HISTORY_LOG_CODES } from '../../../../../@types/service.type';
import { Panel } from '../../../../../components/atoms/Panel';
import { RichTextEditor } from '../../../../../components/atoms/RichTextEditor';
import { PopupDialog } from '../../../../../components/molecules/PopupDialog';
import { isUserHasPermission } from '../../../../../configs/permissions';
import {
  useGetServiceLogsById,
  useHideServiceLog,
  useUpdateServiceLogsById,
} from '../../../../../queries/service-log-query';

const tabSx = {
  fontSize: '12px',
  textTransform: 'none' as const,
  minHeight: 32,
  py: 0.5,
  px: 1.5,
};

interface LogFilter {
  key: string;
  label: string;
  codes: string[];
}

const LOG_FILTERS: LogFilter[] = [
  { key: 'all', label: 'All', codes: [] },
  { key: 'notes', label: 'Notes', codes: ['notes'] },
  { key: 'status', label: 'Status Change', codes: ['status-change', 'status'] },
  {
    key: 'parts',
    label: 'Part Actions',
    codes: [
      'part-actions-install-serialised',
      'part-actions-remove-serialised',
      'part-actions-replace-serialised',
      'part-actions-doa-serialised',
      'part-actions-clean-serialised',
      'part-actions-test-serialised',
      'part-actions-repair-serialised',
      'part-actions-adjust-serialised',
    ],
  },
  {
    key: 'assignment',
    label: 'Assignments',
    codes: ['assignment-events-primary', 'assignment-events-secondary', 'allocation'],
  },
  { key: 'attendance', label: 'Attendance', codes: ['check-in', 'check-out', 'attendance'] },
  { key: 'uploads', label: 'Uploads', codes: ['uploads'] },
  { key: 'interactions', label: 'Interactions', codes: ['interaction-events'] },
];

export const HistoryNotesPanel: React.FC<HistoryNotesPanelProps> = ({
  logs = [],
  serviceId,
  permissions,
  highlightPartActions,
  attendanceMetrics,
}) => {
  const [activeTab, setActiveTab] = useState<HistorySubTab>('history');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>('newest');

  // Edit note state
  const [editLogId, setEditLogId] = useState<number | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const canEditNotes = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Log_Edit_Notes,
    permissions
  );
  const canDeleteNotes = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Log_Hide_Records,
    permissions
  );
  const canViewAttendances = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_Advanced,
    permissions
  );

  // Force history tab if the user loses Attendances access mid-session
  // (e.g. permissions refetch lands while they have it selected).
  useEffect(() => {
    if (!canViewAttendances && activeTab === 'attendances') {
      setActiveTab('history');
    }
  }, [canViewAttendances, activeTab]);

  const getLogByIdQuery = useGetServiceLogsById(editLogId ?? 0);
  const updateLogMutation = useUpdateServiceLogsById();
  const hideLogMutation = useHideServiceLog();

  // When a log is selected for editing, fetch its data. Intentionally keyed on
  // editLogId only: getLogByIdQuery is an `enabled: false` query whose object
  // identity changes every render, so depending on it would refetch in a loop.
  useEffect(() => {
    if (editLogId) getLogByIdQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLogId]);

  useEffect(() => {
    if (getLogByIdQuery.data) {
      setEditNotes(getLogByIdQuery.data.notes ?? '');
      setEditDialogOpen(true);
    }
  }, [getLogByIdQuery.data]);

  const handleEditNote = useCallback(
    (logId: number) => {
      if (canEditNotes) setEditLogId(logId);
    },
    [canEditNotes]
  );

  const handleSaveNote = useCallback(async () => {
    if (!editLogId || !serviceId) return;
    await updateLogMutation.mutateAsync({ id: editLogId, serviceId, notes: editNotes });
    setEditDialogOpen(false);
    setEditLogId(null);
    setEditNotes('');
  }, [editLogId, serviceId, editNotes, updateLogMutation]);

  const handleCloseEditDialog = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    setEditDialogOpen(false);
    setEditLogId(null);
    setEditNotes('');
  }, []);

  const handleDeleteNote = useCallback(
    async (logId: number) => {
      if (!canDeleteNotes) return;
      await hideLogMutation.mutateAsync({ id: logId, isHidden: true });
    },
    [canDeleteNotes, hideLogMutation]
  );

  // Hidden logs are soft-deleted records. The BFF still returns them (the list
  // endpoint applies no isHidden filter), so "hide" only takes effect if the
  // client drops them from view. Filter once here so the chip counts and the
  // table agree. Mirrors the mobile history view (MobileServiceHistory).
  const visibleLogs = useMemo(
    () =>
      logs.filter(
        (log) =>
          !log.isHidden &&
          !HIDDEN_HISTORY_LOG_CODES.has(String(log.logTypeCode ?? log.type ?? '').toLowerCase())
      ),
    [logs]
  );

  // Count logs per filter category
  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: visibleLogs.length };
    for (const f of LOG_FILTERS) {
      if (f.key === 'all') continue;
      counts[f.key] = visibleLogs.filter((log) => {
        const code = String(log.logTypeCode ?? log.type ?? '').toLowerCase();
        return f.codes.some((c) => code.includes(c));
      }).length;
    }
    return counts;
  }, [visibleLogs]);

  // Apply search filter
  const searchFiltered = useMemo(() => {
    if (!searchQuery) return visibleLogs;
    const q = searchQuery.toLowerCase();
    return visibleLogs.filter((log) => {
      const notes = String(log.notes ?? log.description ?? '');
      const user = String(log.userName ?? log.user ?? log.createdBy ?? '');
      return notes.toLowerCase().includes(q) || user.toLowerCase().includes(q);
    });
  }, [visibleLogs, searchQuery]);

  return (
    <Panel title="History & Notes">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Sub-tabs, search, and sort. The Tabs strip is suppressed when the
            user only has access to a single sub-tab (History) — showing one
            lone tab implies further options exist when they don't. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: canViewAttendances ? 'space-between' : 'flex-end',
            gap: 1,
          }}
        >
          {canViewAttendances && (
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val as HistorySubTab)}
              sx={{ minHeight: 32 }}
            >
              <Tab label="History" value="history" sx={tabSx} />
              <Tab label="Attendances" value="attendances" sx={tabSx} />
            </Tabs>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip
              title={sortDirection === 'newest' ? 'Showing newest first' : 'Showing oldest first'}
            >
              <IconButton
                size="small"
                onClick={() => setSortDirection((d) => (d === 'newest' ? 'oldest' : 'newest'))}
                aria-label="Toggle sort direction"
                sx={{ color: 'text.secondary' }}
              >
                <SwapVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              inputProps={{ 'aria-label': 'Search history' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 180,
                '& .MuiOutlinedInput-root': { fontSize: '12px', height: 30 },
              }}
            />
          </Box>
        </Box>

        {/* Filter chips */}
        {activeTab === 'history' && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {LOG_FILTERS.map((f) => {
              const count = filterCounts[f.key] ?? 0;
              if (f.key !== 'all' && count === 0) return null;
              const active = activeFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  label={f.key === 'all' ? f.label : `${f.label} (${count})`}
                  size="small"
                  onClick={() => setActiveFilter(f.key)}
                  sx={{
                    height: 22,
                    fontSize: '10px',
                    fontWeight: active ? 600 : 400,
                    bgcolor: active ? 'v4.accent' : 'v4.tableEven',
                    color: active ? 'common.white' : 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Attendance metrics (only on attendances tab) */}
        {canViewAttendances && activeTab === 'attendances' && (
          <AttendanceMetrics metrics={attendanceMetrics} />
        )}

        {/* History table */}
        <HistoryTable
          logs={searchFiltered}
          serviceId={serviceId}
          filterType={activeTab === 'attendances' ? 'attendance' : activeFilter}
          sortDirection={sortDirection}
          onEditNote={handleEditNote}
          onDeleteNote={handleDeleteNote}
          canEditNotes={canEditNotes}
          canDeleteNotes={canDeleteNotes}
          highlightPartActions={highlightPartActions}
        />
      </Box>

      {/* Edit Note Dialog */}
      <PopupDialog
        title="Edit Note"
        isOpen={editDialogOpen}
        width="85%"
        maxWidth="800px"
        onClose={handleCloseEditDialog}
        onSave={handleSaveNote}
      >
        <RichTextEditor value={editNotes} onValueChange={(value: string) => setEditNotes(value)} />
      </PopupDialog>
    </Panel>
  );
};
